// ─────────────────────────────────────────────────────────────────
// Glass-i — Customer Master grid columns (flat layout).
//
// Single-row column header, no nested groups. Per the latest spec the
// grid drops HSN / SAC and Email and adds Customer Group, Price Group,
// and Sales Representative so the operator can see the classification
// + sales axes inline.
//
// Backed by the server-mode adapter — every `sorter` is `true` (the
// server handles ordering), every `dataIndex` maps to a camelCase
// response key emitted by customer.mapper.js → fromRow.
// ─────────────────────────────────────────────────────────────────

import { Tag } from 'antd';
import {
  STATUS_TAG_COLOUR,
  GST_REGISTRATION_TYPES,
  GST_VERIFICATION_STATUSES
} from './customerConstants';
import { formatINR } from '../../../../components/common/erp-grid';

// Kept for the read-only detail-drawer groups exported below — those
// labels still need the verbose registration-type + verification text
// even though the leaf columns themselves were trimmed in this stage.
const REG_LABEL = Object.fromEntries(
  GST_REGISTRATION_TYPES.map((o) => [o.value, o.label])
);
const VERIFY_LABEL = Object.fromEntries(
  GST_VERIFICATION_STATUSES.map((o) => [o.value, o.label])
);

function renderTag(value, palette, fallback = 'default') {
  if (!value) return <span className="erp-grid__muted">—</span>;
  const color = palette[value] || fallback;
  return <Tag color={color} className="erp-grid__tag">{value}</Tag>;
}

const INR0 = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 });

// Monospace identity value (Mobile) — ink slate, tabular, tight.
function monoValue(value) {
  if (!value) return <span className="erp-grid__muted">—</span>;
  return <span className="cmx-mono">{value}</span>;
}

// Row standing → the left accent stripe + status LED on the identity
// cell. Blocked/inactive dominate; otherwise the live credit signal
// (over → red, ≥80% → amber, else green) carries it.
//   ok · warn · over · off (inactive) · hold (blocked)
function rowStanding(record) {
  const r = record || {};
  const s = String(r.status || '').toLowerCase();
  if (s === 'blocked')  return 'hold';
  if (s === 'inactive') return 'off';
  const limit = Number(r.creditLimit) || 0;
  const used  = Math.max(0, Number(r.outstandingBalance) || 0);
  if (limit > 0 && used > limit) return 'over';
  if (limit > 0 && used >= 0.8 * limit) return 'warn';
  return 'ok';
}

// Credit health from live receivable vs sanctioned limit.
function creditHealth(record) {
  const r = record || {};
  const limit = Number(r.creditLimit) || 0;
  const used  = Math.max(0, Number(r.outstandingBalance) || 0);
  if (limit <= 0) return { state: 'none', limit, used, pct: 0 };
  const pct = Math.min(100, Math.round((used / limit) * 100));
  if (used > limit) return { state: 'over', limit, used, pct: 100 };
  if (pct >= 80)    return { state: 'warn', limit, used, pct };
  return { state: 'ok', limit, used, pct };
}

// ── Identity cell — the primary anchor. A left standing-stripe + the
// customer name. GSTIN, City, and the code each have their own home
// (columns / the detail popup), so this cell stays just name + standing. ──
function renderCustomerCell(name, record) {
  const standing = rowStanding(record);

  return (
    <span className={`cmx-id cmx-id--${standing}`}>
      <span className="cmx-id__stripe" aria-hidden="true" />
      <span className="cmx-id__block">
        <span className="cmx-id__nm" title={name}>{name}</span>
      </span>
    </span>
  );
}

// ── Credit — ONE clean right-aligned line: a conditional utilisation
// tag on the left, then the sanctioned limit as the money value. The tag
// only appears when it has something to say, so a book of zero-balance
// customers reads as a calm money column and colour marks the exceptions:
//   · no limit set          → em-dash only
//   · limit, 0% used        → quiet "0%" tag
//   · partial (< 80%)       → slate "NN%" tag
//   · near (≥ 80%)          → amber "NN%" tag
//   · over limit            → red "+₹X" tag
// The full remainder ("₹X left") moves to a hover tooltip on the cell. ──
function renderCreditCell(v, record) {
  const n = Number(v) || 0;
  if (n <= 0) {
    return <span className="cmx-cr cmx-cr--none"><span className="cmx-cr__amt">—</span></span>;
  }
  const h = creditHealth(record);
  const mod = h.state === 'over' ? 'over' : h.state === 'warn' ? 'warn' : 'ok';

  const tag = h.state === 'over'
    ? `+₹${INR0.format(h.used - h.limit)}`
    : `${h.pct}%`;

  const title = h.state === 'over'
    ? `Over limit by ₹${INR0.format(h.used - h.limit)} · ₹${INR0.format(h.used)} of ₹${INR0.format(h.limit)} used`
    : h.used > 0
      ? `₹${INR0.format(h.limit - h.used)} left · ₹${INR0.format(h.used)} of ₹${INR0.format(h.limit)} used`
      : `Fully available · limit ₹${INR0.format(h.limit)}`;

  return (
    <span className={`cmx-cr cmx-cr--${mod}`} title={title}>
      <span className="cmx-cr__tag">{tag}</span>
      <span className="cmx-cr__amt"><span className="cmx-cr__rs">₹</span>{INR0.format(n)}</span>
    </span>
  );
}

// `currentPage` / `pageSize` drive the S.No column's continuous numbering
// across pages. The grid passes each render a PAGE-RELATIVE index (0-based
// within the current page), so the absolute serial number is
// `(currentPage - 1) * pageSize + index + 1`. CustomerMasterPage rebuilds
// the column tree whenever page or pageSize changes, so the numbers track
// pagination correctly (page 2 @ 25/page starts at 26, etc.).
export function buildCustomerColumns({ currentPage = 1, pageSize = 25 } = {}) {
  // ── Leaf columns ───────────────────────────────────────────────
  // Code column dropped from the grid per spec. The backend column
  // still exists and is surfaced on the View / Edit popup header.

  // Serial number — sequential across the whole list, not per-page. Not
  // sortable (it's a positional index, not a data field); fixed-left so it
  // pins with the Customer anchor when the grid scrolls horizontally.
  const serial = {
    title: '#',
    key: 'serialNo',
    // Narrow box; tight cell padding (via .cmx-col-sno) lets 5 digits
    // (up to 99,999) fit without clipping.
    width: 32,
    align: 'right',
    fixed: 'left',
    className: 'cmx-col-sno',
    render: (_v, _record, index) => (
      <span className="cmx-sno">{(currentPage - 1) * pageSize + index + 1}</span>
    )
  };

  // Primary anchor — name over customer code, with the standing stripe.
  const name = {
    title: 'Customer',
    key: 'customerName',
    dataIndex: 'customerName',
    sortField: 'customer_name',
    sorter: true,
    width: 240,
    fixed: 'left',
    dataType: 'string',
    render: renderCustomerCell
  };

  const gstin = {
    title: 'GSTIN',
    key: 'gstin',
    dataIndex: 'gstin',
    sortField: 'gstin',
    sorter: true,
    width: 170,
    dataType: 'string',
    // URP customers have no GSTIN by definition — show a "URP" marker for
    // the unregistered type so operators can tell "unregistered party"
    // apart from "GSTIN not yet captured" (which renders the em-dash).
    render: (v, r) => {
      if (v) return <span className="cmx-mono">{v}</span>;
      if (r && r.gstRegistrationType === 'unregistered') {
        return <span className="cmx-urp">URP</span>;
      }
      return <span className="erp-grid__muted">—</span>;
    }
  };

  const city = {
    title: 'City',
    key: 'billingCity',
    dataIndex: 'billingCity',
    sortField: 'billing_city',
    sorter: true,
    width: 130,
    dataType: 'string',
    groupable: true,
    groupField: 'billing_city',
    filterField: 'billing_city',
    render: (v) => v
      ? <span className="cmx-place">{v}</span>
      : <span className="erp-grid__muted">—</span>
  };

  const mobile = {
    title: 'Mobile',
    key: 'mobile',
    dataIndex: 'mobile',
    sortField: 'mobile',
    sorter: true,
    width: 140,
    dataType: 'string',
    render: (v) => monoValue(v)
  };

  const customerGroup = {
    title: 'Customer Group',
    key: 'customerCategory',
    dataIndex: 'customerCategory',
    sortField: 'customer_category',
    sorter: true,
    width: 150,
    dataType: 'string',
    groupable: true,
    groupField: 'customer_category',
    render: (v) => v
      ? <span className="cmx-cat">{v}</span>
      : <span className="erp-grid__muted">—</span>
  };

  const priceGroup = {
    title: 'Price Group',
    key: 'priceGroup',
    dataIndex: 'priceGroup',
    sortField: 'price_group',
    sorter: true,
    width: 130,
    dataType: 'string',
    groupable: true,
    groupField: 'price_group',
    render: (v) => v
      ? <span className="cmx-price">{v}</span>
      : <span className="erp-grid__muted">—</span>
  };

  const salesRep = {
    title: 'Sales Ref',
    key: 'salesRepresentativeName',
    dataIndex: 'salesRepresentativeName',
    width: 140,
    ellipsis: true,
    dataType: 'string',
    render: (v) => v
      ? <span className="cmx-place">{v}</span>
      : <span className="erp-grid__muted">—</span>
  };

  // Credit gauge — sanctioned limit + live utilisation in ONE column
  // (replaces the old Credit Limit + Headroom pair). Right-aligned money
  // A single right-aligned line: a conditional utilisation tag + the
  // sanctioned limit. Full detail is in the cell's hover tooltip.
  const creditLimit = {
    title: 'Credit',
    key: 'creditLimit',
    dataIndex: 'creditLimit',
    sortField: 'credit_limit',
    sorter: true,
    width: 140,
    align: 'right',
    dataType: 'number',
    editable: true,
    editorType: 'number',
    aggregate: 'sum',
    // Footer summary now reports the total customer count (across the
    // filtered set) instead of the sum of credit limits. Per-row cells
    // still render each customer's credit limit unchanged.
    aggregateRender: (rows, summary) => {
      // Page-level footer: summary is the backend's full-set summary
      // object → use customer_count. Per-group header: summary is the
      // computed numeric value for THIS group → fall back to the rows
      // array length so each group still shows its member count.
      let count;
      if (summary && typeof summary === 'object' && 'customer_count' in summary) {
        count = Number(summary.customer_count) || 0;
      } else {
        count = Array.isArray(rows) ? rows.length : 0;
      }
      return (
        <span className="erp-grid__inr">
          {count.toLocaleString('en-IN')} {count === 1 ? 'customer' : 'customers'}
        </span>
      );
    },
    render: renderCreditCell
  };

  // The standalone Status column is intentionally dropped. Status now
  // reads two ways without its own column: the identity cell's standing
  // stripe (green / amber / red / grey — see rowStanding) carries active
  // vs inactive vs on-hold, and blocked rows get a full rose row-wash
  // (rowClassName → erp-grid__row--blocked in CustomerMasterPage). The
  // legacy `status` field is still what those signals read from, so
  // reports / exports / inline PATCH / soft-delete stay in lockstep.

  // ── Column order — S.No, then the Customer anchor, identity (GSTIN,
  // City), classification (Group, Price Group, Sales Ref), contact
  // (Mobile), and the Credit gauge closes the row as the standing signal
  // the operator scans for. ──
  return [
    serial,
    name,
    gstin,
    city,
    customerGroup,
    priceGroup,
    salesRep,
    mobile,
    creditLimit
  ];
}

// Read-only detail-drawer groups. Mirrors the form sections so the
// "View" mode of the grid produces a coherent summary card.
export function buildCustomerDetailGroups() {
  const optional = (v) => v || '—';
  return [
    {
      title: 'Basic Details',
      items: [
        { label: 'Code', dataIndex: 'customerCode',
          render: (v) => <code className="erp-grid__code">{v}</code> },
        { label: 'Name', dataIndex: 'customerName' },
        { label: 'Display Name', dataIndex: 'displayName', render: optional },
        { label: 'Category', dataIndex: 'customerCategory', render: optional },
        { label: 'Price Group', dataIndex: 'priceGroup', render: optional },
        { label: 'Status', dataIndex: 'status',
          render: (v) => renderTag(v, STATUS_TAG_COLOUR) }
      ]
    },
    {
      title: 'GST & Legal',
      items: [
        { label: 'Registration Type', dataIndex: 'gstRegistrationType',
          render: (v) => REG_LABEL[v] || v },
        { label: 'GSTIN', dataIndex: 'gstin',
          render: (v) => v ? <code className="erp-grid__code">{v}</code> : '—' },
        { label: 'PAN', dataIndex: 'pan',
          render: (v) => v ? <code className="erp-grid__code">{v}</code> : '—' },
        { label: 'Legal Name', dataIndex: 'gstLegalName', render: optional },
        { label: 'Trade Name', dataIndex: 'gstTradeName', render: optional },
        { label: 'GST State', dataIndex: 'gstStateName',
          render: (_v, r) => r.gstStateName
            ? `${r.gstStateName} (${r.gstStateCode})`
            : '—' },
        { label: 'SEZ', dataIndex: 'isSez',
          render: (v) => v ? 'Yes' : 'No' },
        { label: 'Composition Dealer', dataIndex: 'isCompositionDealer',
          render: (v) => v ? 'Yes' : 'No' },
        { label: 'Verification', dataIndex: 'gstVerificationStatus',
          render: (v) => VERIFY_LABEL[v] || 'Not verified' }
      ]
    },
    {
      title: 'Contact',
      items: [
        { label: 'Contact Person', dataIndex: 'contactPerson', render: optional },
        { label: 'Mobile', dataIndex: 'mobile', render: optional },
        { label: 'Alternate Mobile', dataIndex: 'alternateMobile', render: optional },
        { label: 'Phone', dataIndex: 'phone', render: optional },
        { label: 'Email', dataIndex: 'email', render: optional },
        { label: 'Website', dataIndex: 'website', render: optional }
      ]
    },
    {
      title: 'Billing Address',
      items: [
        { label: 'Line 1', dataIndex: 'billingAddressLine1', render: optional },
        { label: 'Line 2', dataIndex: 'billingAddressLine2', render: optional },
        { label: 'City', dataIndex: 'billingCity', render: optional },
        { label: 'District', dataIndex: 'billingDistrict', render: optional },
        { label: 'State', dataIndex: 'billingState', render: optional },
        { label: 'Pincode', dataIndex: 'billingPincode', render: optional },
        { label: 'Country', dataIndex: 'billingCountry', render: optional }
      ]
    },
    {
      title: 'Shipping Address',
      items: [
        { label: 'Same as Billing', dataIndex: 'sameAsBilling',
          render: (v) => v ? 'Yes' : 'No' },
        { label: 'Line 1', dataIndex: 'shippingAddressLine1', render: optional },
        { label: 'City', dataIndex: 'shippingCity', render: optional },
        { label: 'State', dataIndex: 'shippingState', render: optional },
        { label: 'Pincode', dataIndex: 'shippingPincode', render: optional }
      ]
    },
    {
      title: 'Business Terms',
      items: [
        { label: 'Credit Limit', dataIndex: 'creditLimit',
          render: (v) => <span className="erp-grid__inr">{formatINR(v ?? 0)}</span> },
        { label: 'Credit Days', dataIndex: 'creditDays' },
        { label: 'Payment Terms', dataIndex: 'paymentTerms', render: optional },
        { label: 'Opening Balance', dataIndex: 'openingBalance',
          render: (v) => <span className="erp-grid__inr">{formatINR(v ?? 0)}</span> },
        { label: 'Opening Balance Type', dataIndex: 'openingBalanceType', render: optional }
      ]
    },
    {
      title: 'Remarks',
      items: [
        { label: 'Remarks', dataIndex: 'remarks', render: optional }
      ]
    }
  ];
}
